// Copyright 2026 The Simlin Authors. All rights reserved.
// Use of this source code is governed by the Apache License,
// Version 2.0, that can be found in the LICENSE file.

// Minimal Auth.js client for the Express-served SPA (D5.3/D5.4). Auth.js v5
// ships no official browser/React client for a non-Next app, so this wraps the
// server's /api/auth/* endpoints: read the current session, and initiate
// provider sign-in / sign-out via CSRF-protected form posts -- the same
// mechanism next-auth/react uses under the hood.

import { Tier, parseTier } from '@simlin/core/tier';

const AUTH_BASE = '/api/auth';

export interface SessionUser {
  id?: string;
  name?: string;
  email?: string;
  image?: string;
  // Simlin username/handle (null until onboarding assigns one). Used for model
  // ownership and /:username/:project routes.
  username: string | null;
  tier: Tier;
}

export interface Session {
  user: SessionUser;
  expires?: string;
}

function asString(value: unknown): string | undefined {
  return typeof value === 'string' ? value : undefined;
}

// Normalize the /api/auth/session response into a Session, or null when logged
// out. Auth.js returns null (or an empty/user-less object) for no session, and
// a { user, expires } object otherwise. Pure, so the shape handling is
// unit-testable without a browser.
export function parseSession(data: unknown): Session | null {
  if (!data || typeof data !== 'object') {
    return null;
  }
  const user = (data as { user?: unknown }).user;
  if (!user || typeof user !== 'object') {
    return null;
  }
  const u = user as Record<string, unknown>;
  return {
    user: {
      id: asString(u.id),
      name: asString(u.name),
      email: asString(u.email),
      image: asString(u.image),
      username: asString(u.username) ?? null,
      tier: parseTier(u.tier),
    },
    expires: asString((data as { expires?: unknown }).expires),
  };
}

// Fetch the current session, or null when logged out.
export async function getSession(): Promise<Session | null> {
  const res = await fetch(`${AUTH_BASE}/session`, { credentials: 'same-origin' });
  if (!res.ok) {
    return null;
  }
  return parseSession(await res.json());
}

async function getCsrfToken(): Promise<string> {
  const res = await fetch(`${AUTH_BASE}/csrf`, { credentials: 'same-origin' });
  const data = (await res.json()) as { csrfToken?: unknown };
  return asString(data.csrfToken) ?? '';
}

// Submit a CSRF-protected form post to an Auth.js action, navigating the
// browser so it follows the resulting redirect (to the provider, or back to
// callbackUrl). Imperative shell: touches the DOM directly.
function postForm(action: string, fields: Record<string, string>): void {
  const form = document.createElement('form');
  form.method = 'POST';
  form.action = action;
  for (const [name, value] of Object.entries(fields)) {
    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = name;
    input.value = value;
    form.appendChild(input);
  }
  document.body.appendChild(form);
  form.submit();
}

// Begin sign-in with the given provider (e.g. 'google'). Redirects the browser
// through Auth.js to the provider's consent screen and back to callbackUrl.
export async function signIn(provider: string, callbackUrl: string = window.location.href): Promise<void> {
  const csrfToken = await getCsrfToken();
  postForm(`${AUTH_BASE}/signin/${provider}`, { csrfToken, callbackUrl });
}

// Sign out and return to callbackUrl.
export async function signOut(callbackUrl = '/'): Promise<void> {
  const csrfToken = await getCsrfToken();
  postForm(`${AUTH_BASE}/signout`, { csrfToken, callbackUrl });
}

export interface AuthResult {
  ok: boolean;
  error?: string;
}

function errorFrom(data: unknown, fallback: string): string {
  const err = (data as { error?: unknown } | null | undefined)?.error;
  return typeof err === 'string' ? err : fallback;
}

// Sign in with email + password via the Auth.js Credentials provider WITHOUT
// navigating, so the caller can surface inline errors. Posts the form with
// `json: true` (like next-auth/react's signIn('credentials', {redirect:false}));
// the credentials callback sets the session cookie on success.
//
// We confirm success by the source of truth -- whether a session now exists --
// rather than by parsing the callback's redirect URL. Auth.js's failure-response
// shape varies across versions (sometimes `{error}` with no `error=` in `url`),
// and mis-reading it as success made a wrong password reload into the logged-out
// splash screen instead of showing an inline error.
export async function signInWithCredentials(email: string, password: string): Promise<AuthResult> {
  const csrfToken = await getCsrfToken();
  await fetch(`${AUTH_BASE}/callback/credentials`, {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: new URLSearchParams({ csrfToken, email, password, callbackUrl: window.location.href, json: 'true' }),
  });
  const session = await getSession();
  if (session?.user) {
    return { ok: true };
  }
  return { ok: false, error: 'Invalid email or password' };
}

// Create an email/password account (POST /api/signup). Does not establish a
// session; the caller signs in with the same credentials on success.
export async function signUp(email: string, password: string, name?: string): Promise<AuthResult> {
  const res = await fetch('/api/signup', {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password, name }),
  });
  if (res.ok) {
    return { ok: true };
  }
  return { ok: false, error: errorFrom(await res.json().catch(() => ({})), 'Sign-up failed') };
}

// Request a password reset (POST /api/reset/request). Always resolves ok for a
// syntactically valid email (the server never reveals whether the account
// exists). In development the server returns the reset token directly so the
// flow works without email delivery; callers use it to drive the confirm step.
export async function requestPasswordReset(email: string): Promise<{ ok: boolean; token?: string }> {
  const res = await fetch('/api/reset/request', {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email }),
  });
  const data = (await res.json().catch(() => ({}))) as { token?: unknown };
  return { ok: res.ok, token: typeof data.token === 'string' ? data.token : undefined };
}

// Complete a password reset (POST /api/reset/confirm) with a token + new
// password.
export async function resetPassword(token: string, password: string): Promise<AuthResult> {
  const res = await fetch('/api/reset/confirm', {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token, password }),
  });
  if (res.ok) {
    return { ok: true };
  }
  return { ok: false, error: errorFrom(await res.json().catch(() => ({})), 'Reset failed') };
}
